/**
 * V2 Takeout import (2026-08-03) — Google Takeout's "Saved" export is
 * one CSV per Google Maps list (Title, Note, URL, plus extras that vary
 * by export). This module turns such a file into resolved, addable
 * places:
 *
 *   takeoutRows(text)          CSV text → [{ title, note, url }]
 *   resolveTakeoutRows(rows)   each row → a Google place candidate, via
 *                              the row's URL first (it names the exact
 *                              saved place), then a Text Search on the
 *                              title
 *
 * Resolution is PACED: the google-proxy rate-limits at 100 requests per
 * minute per IP, and one row can cost two calls (link expansion + a
 * search). PACING.delayMs keeps a full-size list safely under that
 * limit; tests zero it.
 */
import { mapsLinkCandidates, textSearchPlaces, matchesName } from '../log/places.js';

// Matching runs in user-driven batches (2026-08-04 on-device feedback: a
// real "Want to go" export arrived with 1,080 rows — the old flat 500-row
// cap both truncated it AND meant a ~12-minute wait before the first
// review). 100 rows ≈ 2 minutes of paced matching per tap.
export const IMPORT_BATCH = 100;

export const PACING = { delayMs: 1400 };

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Minimal RFC-4180 CSV: quoted fields may hold commas, newlines, and
 * doubled quotes. Returns rows of raw string cells; blank lines drop.
 */
export function parseCsv(text) {
  const rows = [];
  let row = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          quoted = false;
        }
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cell);
      cell = '';
      rows.push(row);
      row = [];
    } else {
      cell += ch;
    }
  }
  if (cell !== '' || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

/**
 * A Takeout list CSV as rows to import. Columns are found by header
 * name (Title/Note/URL, any casing, any order, extras ignored) so a
 * format shuffle degrades to "not a list file" rather than garbage.
 * Returns [] for anything that isn't one.
 */
export function takeoutRows(text) {
  const rows = parseCsv(text || '');
  if (!rows.length) return [];
  const header = rows[0].map((h) => h.trim().toLowerCase());
  const ti = header.indexOf('title');
  if (ti === -1) return [];
  const ni = header.indexOf('note');
  const ui = header.indexOf('url');
  return rows
    .slice(1)
    .map((r) => ({
      title: (r[ti] || '').trim(),
      note: ni === -1 ? '' : (r[ni] || '').trim(),
      url: ui === -1 ? '' : (r[ui] || '').trim(),
    }))
    .filter((r) => r.title || r.url);
}

/**
 * One row → { status, place? }:
 *   'matched'   — confident; the review screen pre-checks it
 *   'uncertain' — best guess whose name doesn't match the row's title;
 *                 shown unchecked
 *   'closed'    — matched, but Google says it shut for good; shown
 *                 unchecked so a dead place needs a deliberate tap
 *   'unmatched' — nothing found
 *   'rejected'  — the row's link points at an area, not a place
 * The URL resolves first; a miss falls back to a Text Search on the
 * title. Takeout writes http:// links and the proxy only expands
 * https, so the scheme upgrades on the way in.
 */
export async function resolveTakeoutRow(row) {
  let candidates = [];
  const url = (row.url || '').replace(/^http:\/\//i, 'https://');
  if (url) {
    const viaLink = await mapsLinkCandidates(url).catch(() => null);
    if (viaLink?.rejected) return { status: 'rejected' };
    candidates = viaLink?.candidates || [];
  }
  if (!candidates.length && row.title) {
    candidates = await textSearchPlaces(row.title, null, null, 8).catch(() => []);
  }
  const best = candidates[0];
  if (!best?.placeId) return { status: 'unmatched' };
  const confident = !row.title || matchesName(row.title, best.mainText);
  // Link matches carry businessStatus; the bare title search returns
  // Text Search's own business_status — both mean the same thing here.
  const closed = (best.businessStatus || best.business_status) === 'CLOSED_PERMANENTLY';
  return {
    status: closed ? 'closed' : (confident ? 'matched' : 'uncertain'),
    place: { placeId: best.placeId, mainText: best.mainText, secondaryText: best.secondaryText || '' },
  };
}

/**
 * Resolve rows one at a time (paced — see header). onRow fires after
 * each with (resolvedRow, index, total) so the UI can stream progress;
 * shouldStop() lets a Stop button end the walk early and review what's
 * done so far.
 */
export async function resolveTakeoutRows(rows, { onRow, shouldStop } = {}) {
  const out = [];
  for (let i = 0; i < rows.length; i++) {
    if (shouldStop?.()) break;
    if (i && PACING.delayMs) await sleep(PACING.delayMs);
    if (shouldStop?.()) break;
    const resolved = { ...rows[i], ...(await resolveTakeoutRow(rows[i])) };
    out.push(resolved);
    onRow?.(resolved, i, rows.length);
  }
  return out;
}
